import React, { useState } from "react";
import {
  useImportProjectMutation,
  useLazyGetMatchingApiDataCollectionsQuery,
} from "../../store/slices/settingsSlice";
import MyModal from "../../Common/MyModal";
import { Form, Tab, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import WhiteButton from "../../Common/WhiteButton";
import ContactsIcon from "../../../assets/icons/contact_edit.svg2";
import JsonCollapse from "./JsonCollapse";
import FullScreenLoadingIndicator from "../../Common/FullScreenLoadingIndicator";
import SolutionImportInput from "../../Solutions/SolutionImportInput";
import HelpButton from "../../HelpContent/HelpButton";
import {
  useCreateContactSetMutation,
  useGetContactSetsQuery,
  useLazyFindMatchingContactFieldsQuery,
  useUpsertFieldMutation,
} from "../../store/slices/contactsSlice";
import {
  useCreateReportingTemplateMutation,
  useCreateReportingTopicMutation,
  useGetReportingTopicsQuery,
  useLazyGetMatchingReportingTemplatesQuery,
} from "../../store/slices/reportingSlice";
import {
  useGetApiDataCollectionsQuery,
  useSaveApiDataCollectionMutation,
  useUpdateApiConnectionMutation,
} from "../../store/slices/settingsSlice";
import { handleCreateFields } from "../../Solutions/services/fieldCreationService";
import { handleCheckMatchingFields } from "../../Solutions/services/fieldMatchingService";

export default function ProjectImportModal() {
  const [importedJSON, setImportedJSON] = useState<any>();
  const [manualFields, setManualFields] = useState<string[]>([]);
  const [automaticallySelectedFields, setAutomaticallySelectedFields] =
    useState<string[]>([]);
  const [importProject] = useImportProjectMutation();
  const [loading, setLoading] = useState(false);
  const [apiProfiles, setApiProfiles] = useState<any>({});
  const { data: { contactSets } = { contactSets: [] } } =
    useGetContactSetsQuery({
      page: 0,
      perPage: 100,
    });
  const { data: { data: reportingTopics } = { data: [] } } =
    useGetReportingTopicsQuery({ pagination: { page: 0, perPage: 100 } });
  const { data: { collections: apiDataCollections } = { collections: [] } } =
    useGetApiDataCollectionsQuery({
      search: "",
      offset: 0,
      limit: 100,
    });
  const [updateApiConnection] = useUpdateApiConnectionMutation();
  const [upsertField] = useUpsertFieldMutation();
  const [createContactSet] = useCreateContactSetMutation();
  const [createReportingTemplate] = useCreateReportingTemplateMutation();
  const [createReportingTopic] = useCreateReportingTopicMutation();
  const [saveApiDataCollection] = useSaveApiDataCollectionMutation();
  const [findMatchingContactFields] = useLazyFindMatchingContactFieldsQuery();
  const [findMatchingTemplates] = useLazyGetMatchingReportingTemplatesQuery();
  const [findMatchingApiDataCollections] =
    useLazyGetMatchingApiDataCollectionsQuery();

  const getMatchingSolutionData = (projectJSON: any) => ({
    Components: [
      {
        Type: "project",
        ComponentUuid: projectJSON?.ComponentUuid || projectJSON?.Uuid || "imported-project",
        Bag: projectJSON?.Bag || {},
        Item: projectJSON?.Item || {},
      },
    ],
  });

  const applyDefaultFieldMatches = async (projectJSON: any) => {
    setAutomaticallySelectedFields([]);
    const solutionData = getMatchingSolutionData(projectJSON);
    const updates = await handleCheckMatchingFields(
      projectJSON,
      {
        findMatchingContactFields,
        findMatchingTemplates,
        findMatchingApiDataCollections,
        contactSetsData: { contactSets: contactSets || [] },
        reportingTopicsData: { data: reportingTopics || [] },
        solutionData,
      },
      () => {},
      setAutomaticallySelectedFields
    );

    return {
      ...projectJSON,
      Bag: {
        ...projectJSON.Bag,
        External: { ...projectJSON.Bag.External, ...updates },
      },
    };
  };

  const createRequiredFields = async () => {
    const updates = await handleCreateFields(
      importedJSON,
      manualFields,
      {
        upsertField,
        createContactSet,
        createReportingTemplate,
        createReportingTopic,
        saveApiDataCollection,
        updateApiConnection,
      },
      "",
      () => {}
    );

    const updatedImportedJSON = {
      ...importedJSON,
      Bag: {
        ...importedJSON.Bag,
        External: { ...importedJSON.Bag.External },
      },
    };

    Object.entries(updates || {}).forEach(([fieldType, fieldUpdates]) => {
      Object.entries(fieldUpdates || {}).forEach(([key, value]) => {
        if (updatedImportedJSON.Bag.External[fieldType]) {
          updatedImportedJSON.Bag.External[fieldType][key] = value;
        }
      });
    });

    setImportedJSON(updatedImportedJSON);

    return updatedImportedJSON;
  };

  return (
    <MyModal
      label={
        <WhiteButton
          as="span"
          icon={ContactsIcon}
          heading="Import"
          subheading="Component"
          target={undefined}
          disabled={false}
        />
      }
      heading={
        <span>
          Import Component <HelpButton helpKey="import-component" />
        </span>
      }
      onShow={() => {
        setImportedJSON(undefined);
        setManualFields([]);
        setAutomaticallySelectedFields([]);
      }}
      size="xxl"
    >
      {({ handleClose }) => (
        <>
          <FullScreenLoadingIndicator loading={loading} />
          <Form.Control
            className="mb-3"
            type="file"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setLoading(true);
              const reader = new FileReader();
              reader.readAsText(file, "UTF-8");
              reader.onload = async function ({ target }) {
                try {
                  const parsedJSON = JSON.parse(target?.result as string);
                  setImportedJSON(await applyDefaultFieldMatches(parsedJSON));
                } catch (error) {
                  toast.error("Unable to import component JSON");
                  setImportedJSON(undefined);
                } finally {
                  setLoading(false);
                }
              };
              reader.onerror = function () {
                setImportedJSON(undefined);
                setLoading(false);
              };
            }}
          />
          {!!importedJSON && (
            <div
              className="bg-white p-2 rounded-2"
              style={{ maxHeight: "700px", overflowY: "auto" }}
            >
              <Tabs mountOnEnter>
                {Object.entries(importedJSON?.Bag?.External || {}).map(
                  ([fieldType, fields]) => {
                    if (
                      fieldType === "DataStructureField" ||
                      fieldType === "ReportingTemplateField"
                    )
                      return null;

                    const entries = Object.entries(fields || {}) as [
                      string,
                      any,
                    ][];
                    if (!entries.length) return null;

                    return (
                      <Tab
                        key={fieldType}
                        eventKey={fieldType}
                        title={
                          fieldType === "ContactField"
                            ? "Contact fields"
                            : fieldType === "ContactSet"
                              ? "Contact Sets"
                              : fieldType === "ReportingTemplate"
                                ? "Reporting Templates"
                                : fieldType === "Topic"
                                  ? "Reporting Topics"
                                  : fieldType === "DataStructure"
                                    ? "Data Structures"
                                    : fieldType === "ApiConnection"
                                      ? "API Connection"
                                      : fieldType
                        }
                      >
                        <div className="p-3 bg-purple-gradient rounded-2">
                          {entries.map(([fieldKey, value]) => (
                            <SolutionImportInput
                              key={fieldKey}
                              fieldType={fieldType}
                              fieldKey={fieldKey}
                              value={value}
                              importedJSON={importedJSON}
                              setImportedJSON={setImportedJSON}
                              manualFields={manualFields}
                              setManualFields={setManualFields}
                              automaticallySelectedFields={
                                automaticallySelectedFields
                              }
                              setAutomaticallySelectedFields={
                                setAutomaticallySelectedFields
                              }
                              contactSets={contactSets || []}
                              reportingTopics={reportingTopics || []}
                              apiDataCollections={apiDataCollections || []}
                              apiProfiles={apiProfiles}
                              setApiProfiles={setApiProfiles}
                              updateApiConnection={updateApiConnection as any}
                            />
                          ))}
                        </div>
                      </Tab>
                    );
                  }
                )}
              </Tabs>
            </div>
          )}
          <button
            className="btn btn-colored"
            disabled={!importedJSON}
            onClick={async () => {
              if (!importedJSON) return;
              setLoading(true);

              try {
                const updatedImportedJSON = await createRequiredFields();
                const result: any = await importProject(updatedImportedJSON);

                if (result?.data) {
                  toast.success("Project imported successfully");
                  handleClose();
                }
              } finally {
                setLoading(false);
              }
            }}
          >
            Import Component
          </button>
        </>
      )}
    </MyModal>
  );
}
